import { Emitter, Event } from "../../../base/common/events";
import { Disposable } from "../../../base/common/lifecycle";
import { CommandsRegistry, ICommandService } from "../../commands/common/commands";
import { ILogService } from "../../log/common/log";
import { AIToolCallContext, AIToolCallResponse, AIToolServiceChangedEvent, IAITool, IAIToolService } from "./ai";
import { createToolFromCommand } from "./tools/commands.js";



export class AIToolService extends Disposable implements IAIToolService {

    constructor(
        @ILogService protected readonly logger: ILogService,
        @ICommandService protected readonly commands: ICommandService,
    ) {
        super();
        this._commandToolsNeedUpdate = true;
        this._register(this.commands.onDidChange(e => {
            this._commandToolsNeedUpdate = true;
        }));
        // A command registered AFTER the first getAvailableTools() must still
        // become a tool. commandService.onDidChange does NOT fire on
        // registration, so without this a command registered late (the export
        // command is added in the ThreeJs engine ctor, after the property/
        // hierarchy services) was cached-out forever. Invalidate on the registry
        // event and notify listeners (the MCP surface re-registers on it).
        this._register(CommandsRegistry.onDidRegisterCommand(() => {
            this._commandToolsNeedUpdate = true;
            this._onDidChange.fire({ service: this });
        }));
    }

    private _commandToolsNeedUpdate = false;
    private readonly _registeredTools: IAITool[] = [];
    /** COMMAND ID -> its tool. Keyed by the id, never by the tool name: a
     *  command with `aiName` presents a different name than its id, and a
     *  name-keyed cache never recognised its own tool (see the update below). */
    private readonly _commandTools = new Map<string, IAITool>();




    private readonly _onDidChange = new Emitter<AIToolServiceChangedEvent>();
    readonly onDidChange = this._onDidChange.event;

    registerTool(tool: IAITool): void {
        if (!tool) {
            this.logger.error("Attempted to register null or undefined AI tool.");
            return;
        }
        else if (!tool.name) {
            this.logger.error("Attempted to register AI tool with no name.");
            return;
        }
        else if (this._registeredTools.find(t => t.name === tool.name)) {
            this.logger.warn(`AI tool with name '${tool.name}' is already registered. Skipping.`);
            return;
        }

        if (!this._registeredTools.includes(tool)) {
            this.logger.debug(`Registering AI tool '${tool.name}'.`);
            this._registeredTools.push(tool);
        }
    }

    async getAvailableTools(): Promise<IAITool[]> {
        this.updateToolsFromRegisteredCommandsIfNecessary();
        return [
            ...this._registeredTools,
            ...this._commandTools.values(),
        ].filter(tool => {
            return tool.canRun ? tool.canRun() : true;
        })
    }

    callTool(toolName: string, parameters: object, context?: AIToolCallContext): Promise<AIToolCallResponse> {
        const tool = this.findToolByName(toolName);
        if (!tool) {
            return Promise.reject(new Error(this.unknownToolMessage(toolName)));
        }

        return tool.call(parameters, context)
            .catch(err => {
                this.logger.error(`Error calling tool '${toolName}':`, err);
                throw err;
            });;
    }

    private findToolByName(toolName: string): IAITool | null {
        const tool = this._registeredTools.find(t => t.name === toolName);
        if (tool) {
            return tool;
        }
        this.updateToolsFromRegisteredCommandsIfNecessary();
        for (const generatedTool of this._commandTools.values()) {
            if (generatedTool.name === toolName) return generatedTool;
        }

        return null;
    }

    /**
     * A miss must be RECOVERABLE. The namespace is all-underscore now, but a
     * model still drifts to dots or other casing (and resumed conversations
     * carry the pre-rename spellings) — and a bare "not found" reads as the
     * tooling having disappeared: live on 2026-09-01 the chat called
     * `project_searchFiles` (then not a name), concluded it had lost file
     * access, told the user so and pasted three broken scripts into the
     * conversation instead of writing the file.
     */
    private unknownToolMessage(toolName: string): string {
        const wanted = normalizeToolName(toolName);
        for (const tool of [...this._registeredTools, ...this._commandTools.values()]) {
            if (normalizeToolName(tool.name) === wanted && tool.name !== toolName) {
                return `Tool '${toolName}' not found. Did you mean '${tool.name}'?`;
            }
        }
        return `Tool '${toolName}' not found.`;
    }

    private updateToolsFromRegisteredCommandsIfNecessary() {
        if (this._commandToolsNeedUpdate) {
            this._commandToolsNeedUpdate = false;

            const commandList = CommandsRegistry.getCommands();

            const generatedCommands = new Array<string>();

            for (const [key] of commandList) {
                // by COMMAND ID: this asked `t.name === key` until 2026-09-01,
                // which never matched a command declaring `aiName` (its tool is
                // called something else), so every refresh appended another copy
                // — `property_write` reached the model twice
                if (this._commandTools.has(key)) {
                    // this.logger.debug(`AI tool for command '${key}' already exists. Skipping tool generation.`);
                    continue; // Tool for this command already exists
                }
                const tool = createToolFromCommand(key, this.commands, this.logger);
                if (tool) {
                    // this.logger.debug(`Generated AI tool for command '${key}':`, tool);
                    this._commandTools.set(key, tool);
                    generatedCommands.push(key);
                }
            }

            // a command that is no longer registered must stop being offered
            for (const key of [...this._commandTools.keys()]) {
                if (!commandList.has(key)) this._commandTools.delete(key);
            }

            if (generatedCommands.length) {
                this.logger.debug(`AI Tool Service: Generated tools for commands`, generatedCommands);
            }

        }
    }


}

/** Two tool names are "the same name, spelled differently" when they differ
 *  only in separators or case — the drift a mixed `a.b` / `a_b` namespace
 *  invites. Deliberately not fuzzy: a near-miss suggestion that is wrong is
 *  worse than none. */
function normalizeToolName(name: string): string {
    return name.toLowerCase().replace(/[._-]/g, "");
}
